import ArticleLayout from '../../components/ArticleLayout';

export default function RadiatorLaekker() {
  return (
    <ArticleLayout
      title="Radiatoren lækker vand: Sådan stopper du skaden her og nu"
      metaTitle="Radiator lækker vand? Akut førstehjælp og løsning"
      metaDescription="Drypper det fra radiatoren, ventilen eller pakdåsen? Se hvordan du straks begrænser vandskaden, afspærrer radiatoren og tilkalder en fagmand."
      canonicalPath="/radiator-laekker"
      introLead="En lækkende radiator kræver øjeblikkelig handling for at forhindre fugtskader i trægulve, lofter og vægge under installationen."
      introAnswer="Start altid med at placere en spand eller en dyb bradepande under drypstedet og læg håndklæder ud. Lokaliser dernæst det præcise lækagested: De fleste dryp stammer fra en slidt pakdåse ved ventilen, som let kan afspærres og udskiftes, mens en gennemtæret radiatorkrop kræver komplet udskiftning."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Akut førstehjælp ved lækage' },
        { id: 'hvor-laekker', title: '2. De fire mest almindelige lækagesteder' },
        { id: 'afspaerring', title: '3. Sådan isolerer du radiatoren fra anlægget' },
        { id: 'udskiftning-pakdaase', title: '4. Pakdåsen: Det eneste du selv kan fikse' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 akutte trin ved vanddryp fra radiatoren"
      checklist={[
        {
          title: 'Placer straks en spand og sugende håndklæder under lækagen',
          description: 'Hurtig opsamling af vandet begrænser skader på parketgulve og forhindrer nedsivning til underboen.',
        },
        {
          title: 'Tør området tørt med køkkenrulle for at finde præcis hvor vandet pibler ud',
          description: 'Undersøg om vandet kommer fra stiften ved termostaten, luftskruen, rørgevindet eller bunden af radiatoren.',
        },
        {
          title: 'Luk termostathovedet helt i bund (skru mod 0 eller ✱)',
          description: 'Dette lukker for ventilsædet og standser trykket af nyt fremløbsvand ind i radiatoren.',
        },
        {
          title: 'Luk returventilen i bunden af radiatoren med en unbrakonøgle',
          description: 'Fjern dækhætten på bunden og skru unbrakoskruen med uret i bund for at stoppe tilbageløb.',
        },
        {
          title: 'Kontakt en autoriseret VVS-installatør til den endelige udbedring',
          description: 'Oplys installatøren om dryppet er ved en pakning, ventilen eller om der er rusthul i selve stålet.',
        },
      ]}
      sections={[
        {
          id: 'hvor-laekker',
          title: 'De fire typiske lækagesteder og hvad de betyder',
          content: (
            <>
              <p>
                Et vanddryp ser ofte værre ud end det er. Ved at tørre metaldelene helt af kan du hurtigt afgøre alvorsgraden:
              </p>
              <p>
                <strong>1. Dryp fra ventilstiften (pakdåsen):</strong> Dette er den hyppigste og mest harmløse lækage. Pakdåsens indvendige o-ring bliver mør efter 10–15 år. Det drypper typisk langs den lille stift bag termostathovedet.
              </p>
              <p>
                <strong>2. Dryp fra luftskruen:</strong> Hvis luftskruen ikke er lukket stramt til efter udluftning, eller pakgarnet i gevindet er udtørret, pibler vandet ud ved udlufterproppen.
              </p>
              <p>
                <strong>3. Utæthed i rørforskruninger (unioner):</strong> Samlingerne mellem varmerør og ventilhus er forseglet med pakgarn og paksalve. Efter mange årtiers vibrationer kan samlingen begynde at svede eller dryppe.
              </p>
              <p>
                <strong>4. Rusthul og gennemtæring i selve radiatorkroppen:</strong> Optræder oftest nederst på radiatorens panel eller i svejsningerne, ledsaget af brune rustbobler under malingen. Dette er et tegn på, at radiatoren er gennemtæret indefra og skal udskiftes omgående.
              </p>
            </>
          ),
        },
        {
          id: 'afspaerring',
          title: 'Sådan isolerer du radiatoren fra resten af husets varmeanlæg',
          content: (
            <>
              <p>
                Hvis en radiator drypper kraftigt, kan du isolere den, så resten af huset stadig har varme:
              </p>
              <p>
                1. <strong>Luk toppen:</strong> Drej termostaten i bund (på 0). Hvis termostathovedet er taget af, kan du sætte en transport-beskyttelseshætte på og skrue den fast i bund for at presse stiften ind.
              </p>
              <p>
                2. <strong>Luk bunden:</strong> Skru metallåget af returforskruningen forneden. Indeni er der en unbrakoskrue. Skru den med uret, indtil den møder fast modstand.
              </p>
              <p>
                Nu er radiatoren lukket af i begge ender. Det vand, der eventuelt drypper ud, er kun den begrænsede mængde vand, der befinder sig inde i selve radiatoren.
              </p>
            </>
          ),
        },
        {
          id: 'udskiftning-pakdaase',
          title: 'Udskiftning af pakdåse på Danfoss RA-ventiler',
          content: (
            <>
              <p>
                Stammer dryppet udelukkende fra ventilstiften, kan du på moderne Danfoss-ventiler skifte pakdåsen helt uden at tappe vand af varmeanlægget.
              </p>
              <p>
                Ventilhuset har en indbygget bagtætning, der forsegler, når pakdåsen skrues ud. Du skruer den gamle pakdåse ud med en <span className="font-mono">10 mm</span> fastnøgle, monterer en ny original Danfoss servicemodel (koster ca. 40–80 kr.), og spænder den forsigtigt til. Der kan pible en teskefuld vand ud under skiftet, men strømmen stopper så snart gevindet griber.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Akut VVS-udkald ved gennemtæring og rørbrud',
        content: (
          <p>
            Hvis der er opstået et decideret rusthul i radiatorens krop, eller hvis et varmerør er knækket, kan det ikke lappes med tape, silikone eller spændebånd under driftstryk. Luk straks boligens hovedventiler for varme i kælderen eller bryggerset, og tilkald en autoriseret VVS-installatør med døgnservice.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Kan man tætne et rusthul midlertidigt med silikone eller epoxy?',
          a: 'Nej. Vandet i et varmeanlæg står under tryk og er varmt. Hverken silikone, gaffatape eller reparationspasta kan modstå trykket og varmen, og forsøget forværrer ofte skaden.',
        },
        {
          q: 'Hvad dækker husforsikringen ved en utæt radiator?',
          a: 'De fleste bygningsforsikringer dækker følgeskader efter pludselige vandudslip (fx ødelagte gulve og lofter), men dækker normalt ikke selve den tærede radiator eller slidte pakdåser.',
        },
        {
          q: 'Hvorfor er lækagevandet mørkebrunt eller sort?',
          a: 'Vandet i varmerørene har cirkuleret uden ilt i årevis og indeholder magnetit (jernoxid). Det misfarver hurtigt tekstiler og trægulve, så det skal tørres op omgående.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-ventil-sidder-fast', text: 'Ventilstiften sidder fast', desc: 'sådan virker ventilens pakdåse og stift' },
        { to: '/radiator-luftskrue-sidder-fast', text: 'Luftskruen sidder fast', desc: 'forholdsregler ved udlufterventilen' },
        { to: '/radiator-udskiftning-pris', text: 'Hvad koster en ny radiator?', desc: 'prisoverslag på udskiftning og VVS-arbejde' },
        { to: '/radiator-tryk', text: 'Tryktab i anlægget', desc: 'hvad lækager gør ved vandtrykket' },
      ]}
      sources={[
        { url: 'https://www.bolius.dk/valg-af-radiator-19095', label: 'Valg af radiator - sådan sikrer du, at du vælger rigtigt' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/gode-raad-om-drift-af-dit-gasfyr', label: 'Gode råd om drift af dit gasfyr | SparEnergi' },
      ]}
    />
  );
}
